import React, { useState, useEffect, useCallback, useRef } from "react";
import { StaticNote } from "./ui";
import { fetchOvertime } from "../api";
import "./Dashboard.css";

const ROLES = ["CNA", "LPN", "RN"];
const money2 = (n) => (n || n === 0 ? `$${Number(n).toFixed(2)}` : "--");
const monthLabel = (m) => {
  const [y, mo] = String(m).split("-").map(Number);
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
};

// A role count cell — muted when zero, emphasized when there's overtime.
const Count = ({ n }) => <td className={n > 0 ? "strong" : "muted"}>{n || 0}</td>;

// Overtime table: per clinician type, the overtime-shift count AND the average
// pay rate for that role at that client (not blended across roles).
function OvertimeTable({ divisions, byDivision, rows, totals }) {
  return (
    <div className="pl-scroll">
      <table className="grid pl-table ot-grid">
        <thead>
          <tr>
            <th rowSpan={2}>Division / Client</th>
            {ROLES.map((r) => <th key={r} colSpan={2} className="role-group">{r}</th>)}
            <th rowSpan={2} className="strong">Total OT</th>
          </tr>
          <tr>
            {ROLES.map((r) => (
              <React.Fragment key={r}>
                <th className="sub">OT</th>
                <th className="sub">Avg $</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {divisions.map((div) => {
            const dr = rows.filter((r) => r.division === div);
            if (!dr.length) return null;
            const sub = byDivision[div] || { CNA: 0, LPN: 0, RN: 0, total: 0 };
            return (
              <React.Fragment key={div}>
                <tr className="row-highlight"><td className="rowhead strong" colSpan={8}>{div}</td></tr>
                {dr.map((r) => (
                  <tr key={div + r.client}>
                    <td className="rowhead indent">{r.client}</td>
                    {ROLES.map((role) => (
                      <React.Fragment key={role}>
                        <Count n={r[role]} />
                        <td className="muted" title={r.roleShifts?.[role] ? `${r.roleShifts[role]} ${role} shifts` : "no shifts"}>
                          {r.avgPay?.[role] != null ? money2(r.avgPay[role]) : "—"}
                        </td>
                      </React.Fragment>
                    ))}
                    <td className="strong">{r.total}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td className="rowhead">{div} subtotal</td>
                  {ROLES.map((role) => (
                    <React.Fragment key={role}><td>{sub[role]}</td><td></td></React.Fragment>
                  ))}
                  <td className="strong">{sub.total}</td>
                </tr>
              </React.Fragment>
            );
          })}
          <tr className="total-row grand">
            <td className="rowhead strong">All divisions</td>
            {ROLES.map((role) => (
              <React.Fragment key={role}><td className="strong">{totals[role]}</td><td></td></React.Fragment>
            ))}
            <td className="strong">{totals.total}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// Supervisory table: counts only (no pay averages).
function SupTable({ divisions, byDivision, rows, totals }) {
  return (
    <div className="pl-scroll">
      <table className="grid pl-table">
        <thead>
          <tr>
            <th>Division / Client</th>
            {ROLES.map((r) => <th key={r}>{r}</th>)}
            <th className="strong">Total</th>
          </tr>
        </thead>
        <tbody>
          {divisions.map((div) => {
            const dr = rows.filter((r) => r.division === div);
            if (!dr.length) return null;
            const sub = byDivision[div] || { CNA: 0, LPN: 0, RN: 0, total: 0 };
            return (
              <React.Fragment key={div}>
                <tr className="row-highlight"><td className="rowhead strong" colSpan={5}>{div}</td></tr>
                {dr.map((r) => (
                  <tr key={div + r.client}>
                    <td className="rowhead indent">{r.client}</td>
                    {ROLES.map((role) => <Count key={role} n={r[role]} />)}
                    <td className="strong">{r.total}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td className="rowhead">{div} subtotal</td>
                  {ROLES.map((role) => <td key={role}>{sub[role]}</td>)}
                  <td className="strong">{sub.total}</td>
                </tr>
              </React.Fragment>
            );
          })}
          <tr className="total-row grand">
            <td className="rowhead strong">All divisions</td>
            {ROLES.map((role) => <td key={role} className="strong">{totals[role]}</td>)}
            <td className="strong">{totals.total}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function Overtime({ ctx }) {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const retryRef = useRef(null);

  const load = useCallback(async (m) => {
    setLoading(true); setErr(null);
    try {
      const res = await fetchOvertime(ctx, m);
      if (res.warming) {
        setData({ warming: true });
        retryRef.current = setTimeout(() => load(m), 8000); // poll until the shift cache is warm
        return;
      }
      setData(res);
      setMonth(res.month);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    return () => clearTimeout(retryRef.current);
  }, [load]);

  const onMonth = (e) => { setMonth(e.target.value); load(e.target.value); };

  if (err) return <div className="dash"><div className="state error">Error: {err}</div></div>;
  if (data?.warming) {
    return (
      <div className="dash">
        <h1 className="pl-title">Overtime &amp; Supervisory Shifts</h1>
        <section className="panel" style={{ textAlign: "center", padding: "40px 20px" }}>
          <div style={{ fontSize: 30, marginBottom: 8 }}>⏳</div>
          <h2>Loading shift data from Teambridge…</h2>
          <p className="muted">The first load pulls every shift and can take a minute or two. This will refresh automatically.</p>
        </section>
      </div>
    );
  }
  if (loading && !data) return <div className="dash"><div className="state">Loading overtime…</div></div>;
  if (!data) return null;

  const br = data.baseRates || {};
  const divisions = ["Facility", "GEM"];
  const monthsDesc = [...(data.months || [])].sort().reverse();

  return (
    <div className="dash">
      <div className="pl-header">
        <div>
          <h1 className="pl-title">Overtime &amp; Supervisory Shifts</h1>
          <p className="pl-sub">Shifts paid <em>above</em> the base rate for their role &amp; day type. Live from Teambridge · assigned shifts only.</p>
        </div>
        <label className="month-pick">
          Month&nbsp;
          <select className="month-select" value={month || data.month} onChange={onMonth}>
            {monthsDesc.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </label>
      </div>

      <section className="kpis kpis-3">
        <div className="kpi kpi-primary">
          <div className="kpi-label">Overtime shifts (above base)</div>
          <div className="kpi-value">{data.overtime.totals.total}</div>
          <div className="kpi-sub">CNA {data.overtime.totals.CNA} · LPN {data.overtime.totals.LPN} · RN {data.overtime.totals.RN}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Supervisory shifts (SUP)</div>
          <div className="kpi-value">{data.supervisory.totals.total}</div>
          <div className="kpi-sub">tracked separately</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Base rates (wkday / wknd)</div>
          <div className="kpi-sub" style={{ marginTop: 6, lineHeight: 1.6 }}>
            {ROLES.map((r) => br[r] && <div key={r}><b>{r}</b> ${br[r].weekday} / ${br[r].weekend}</div>)}
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-head"><h2>Overtime shifts by division &amp; client</h2><span>OT = shifts above base · Avg $ = average pay rate for that clinician type</span></div>
        <OvertimeTable divisions={divisions} byDivision={data.overtime.byDivision} rows={data.overtime.rows} totals={data.overtime.totals} />
      </section>

      <section className="panel">
        <div className="panel-head"><h2>Supervisory shifts (SUP)</h2><span>service code contains “SUP” · not counted as overtime</span></div>
        {data.supervisory.rows.length ? (
          <SupTable divisions={divisions} byDivision={data.supervisory.byDivision} rows={data.supervisory.rows} totals={data.supervisory.totals} />
        ) : (
          <p className="muted" style={{ padding: "4px 2px" }}>No supervisory shifts this month.</p>
        )}
      </section>

      <StaticNote>
        Overtime = a shift whose Pay Rate exceeds the base rate for its role and day type (weekday/weekend).
        Supervisory shifts (Teambridge service code containing “SUP”) are excluded from overtime and shown separately.
        “Avg $” is the average pay rate for that clinician type at that client (across all of the role’s assigned shifts), not blended across roles. Source: {data.source}.
      </StaticNote>
    </div>
  );
}
